import { useState } from "react";
import { Check, Loader2, Send } from "lucide-react";

const SERVICES = ["Website", "Meta Ads", "Both"];

const inputCls =
  "w-full rounded-xl border border-white/10 bg-[#0A0A0C] px-4 py-3 text-sm text-white placeholder-neutral-600 outline-none transition-[border-color] duration-300 focus:border-[#D4AF37]/60";
const labelCls = "mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500";

export default function QuoteForm() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", business: "", message: "", website: "" });
  const [services, setServices] = useState([]);
  const [status, setStatus] = useState("idle");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggleService = (s) =>
    setServices(services.includes(s) ? services.filter((x) => x !== s) : [...services, s]);

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/inquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, services }),
      });
      if (!res.ok) throw new Error("bad response");
      setStatus("done");
    } catch (err) {
      setStatus("error");
    }
  };

  if (status === "done") {
    return (
      <div
        data-testid="quote-success"
        className="flex h-full min-h-[420px] flex-col items-center justify-center rounded-[2rem] border border-[#D4AF37]/30 bg-[#0F0F12] p-10 text-center"
      >
        <span className="flex h-14 w-14 items-center justify-center rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10">
          <Check className="h-6 w-6 text-[#E5C158]" />
        </span>
        <h3 className="mt-6 font-display text-2xl font-light tracking-tight">
          Thanks{form.name ? `, ${form.name.split(" ")[0]}` : ""} — inquiry received.
        </h3>
        <p className="mt-3 max-w-sm text-sm leading-relaxed text-neutral-400">
          I'll get back to you within 24 hours with next steps and a straight
          answer on cost.
        </p>
        <button
          data-testid="quote-send-another"
          onClick={() => {
            setStatus("idle");
            setForm({ name: "", email: "", phone: "", business: "", message: "" });
            setServices([]);
          }}
          className="mt-8 rounded-full border border-white/15 px-6 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-300 transition-[border-color,color] duration-300 hover:border-[#D4AF37]/60 hover:text-[#E5C158]"
        >
          Send another
        </button>
      </div>
    );
  }

  return (
    <form
      data-testid="quote-form"
      onSubmit={submit}
      className="rounded-[2rem] border border-white/10 bg-[#0F0F12] p-8 sm:p-10"
    >
      <div className="absolute -left-[9999px] -top-[9999px]" aria-hidden="true">
        <label htmlFor="q-website">Website</label>
        <input
          id="q-website"
          name="website"
          tabIndex={-1}
          autoComplete="off"
          data-testid="quote-website-honeypot"
          value={form.website}
          onChange={set("website")}
        />
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label className={labelCls} htmlFor="q-name">
            Name *
          </label>
          <input
            id="q-name"
            data-testid="quote-name"
            required
            value={form.name}
            onChange={set("name")}
            placeholder="Your name"
            className={inputCls}
          />
        </div>
        <div>
          <label className={labelCls} htmlFor="q-business">
            Business
          </label>
          <input
            id="q-business"
            data-testid="quote-business"
            value={form.business}
            onChange={set("business")}
            placeholder="Your business (optional)"
            className={inputCls}
          />
        </div>
        <div>
          <label className={labelCls} htmlFor="q-email">
            Email *
          </label>
          <input
            id="q-email"
            type="email"
            data-testid="quote-email"
            required
            value={form.email}
            onChange={set("email")}
            placeholder="you@company.com"
            className={inputCls}
          />
        </div>
        <div>
          <label className={labelCls} htmlFor="q-phone">
            Phone
          </label>
          <input
            id="q-phone"
            data-testid="quote-phone"
            value={form.phone}
            onChange={set("phone")}
            placeholder="04.. (optional)"
            className={inputCls}
          />
        </div>
      </div>

      <div className="mt-5">
        <span className={labelCls}>What do you need?</span>
        <div className="flex flex-wrap gap-2.5">
          {SERVICES.map((s) => (
            <button
              type="button"
              key={s}
              data-testid={`quote-service-${s.toLowerCase().replace(" ", "-")}`}
              onClick={() => toggleService(s)}
              className={`rounded-full border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] transition-[border-color,color,background-color] duration-300 ${
                services.includes(s)
                  ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#E5C158]"
                  : "border-white/10 text-neutral-400 hover:border-[#D4AF37]/40"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-5">
        <label className={labelCls} htmlFor="q-message">
          Your project *
        </label>
        <textarea
          id="q-message"
          required
          rows={4}
          data-testid="quote-message"
          value={form.message}
          onChange={set("message")}
          placeholder="What are you selling, who are your customers, what does success look like?"
          className={`${inputCls} resize-none`}
        />
      </div>

      {status === "error" && (
        <p
          data-testid="quote-error"
          className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-red-400"
        >
          Something went wrong — email me directly at CFDigiCo@gmail.com.
        </p>
      )}

      <button
        type="submit"
        disabled={status === "sending"}
        data-testid="quote-submit"
        className="mt-7 flex w-full items-center justify-center gap-3 rounded-full bg-[#D4AF37] px-8 py-4 font-mono text-[11px] uppercase tracking-[0.2em] text-black transition-[background-color,box-shadow] duration-300 hover:bg-[#E5C158] hover:shadow-[0_0_40px_rgba(212,175,55,0.35)] disabled:opacity-60"
      >
        {status === "sending" ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Send className="h-3.5 w-3.5" />
        )}
        {status === "sending" ? "Sending…" : "Send inquiry"}
      </button>
    </form>
  );
}